import { User } from '@twilio/conversations';
import { of, Subject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';

import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  EventEmitter,
  Output,
  ViewChild,
  ChangeDetectorRef,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { SnAbilityService } from '@lib/ability';
import { SnUser, SnWidget } from '@lib/api/entities';
import { SnCurrentUser } from '@lib/auth';
import { SnCollection } from '@lib/common/models/collection';
import { ChannelsToShareDialogComponent } from '@lib/communication/twiliochat/components/channels-to-share-dialog/channels-to-share-dialog.component';
import { SnChannel } from '@lib/communication/twiliochat/models/channel.model';
import { SnChatUser } from '@lib/communication/twiliochat/models/chat-user.model';
import { SnChannelsService } from '@lib/communication/twiliochat/services/channels.service';
import { SnChatService } from '@lib/communication/twiliochat/services/chat.service';
import { SnMembersService } from '@lib/communication/twiliochat/services/members.service';

@Component({
  selector: 'sn-chat-channel-widget',
  templateUrl: './chat-channel-widget.component.html',
  styleUrls: ['./chat-channel-widget.component.scss'],
})
export class ChatChannelWidgetComponent implements OnInit, OnDestroy {
  set channelId(value: string) {
    this._channelId = value;
    this._loadChannel();
  }

  get channelId(): string {
    return this._channelId;
  }

  @Input() public widget: SnWidget;

  @Input() public readonly viewId: string;

  @Output() public readonly widgetUpdated = new EventEmitter();

  @ViewChild('chatChannel') public chatChannel;

  public channel: SnChannel;
  public user: User;
  public changeTitle = false;
  public userHasAccess: boolean;
  public readonly canSelect: boolean;
  public readonly canChange: boolean;
  public showMembers: boolean;
  private _channelId: string;
  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _chatService: SnChatService,
    private readonly _cd: ChangeDetectorRef,
    private readonly _dialog: MatDialog,
    private readonly _ability: SnAbilityService,
    private readonly _channelsService: SnChannelsService,
    private readonly _membersService: SnMembersService,
    private readonly _currentUser: SnCurrentUser,
  ) {
    this.canSelect = this._ability.can({
      subject: 'communications/chatchannel',
      action: ['view', 'change'],
    });
    this.canChange = this._ability.can({ subject: 'dashboards/widgetobject', action: 'change' });
    this.userHasAccess = this._ability.can({
      subject: 'communications/chatchannel',
      action: 'view',
    });
  }

  public ngOnInit(): void {
    this._chatService.chatIsReady$.pipe(takeUntil(this._destroy$)).subscribe(() => {
      this.user = this._chatService.ownTwilioUser;
      if (this.widget.meta.channelId) {
        this.channelId = this.widget.meta.channelId;
        this._loadChannel();
      }
      this._cd.detectChanges();
    });
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public selectChannel(): void {
    this._dialog
      .open(ChannelsToShareDialogComponent)
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((channel) => {
        if (!channel?.id) {
          return;
        }
        this.channel = channel;
        this.channelId = channel.sid;
        this.widget.meta.channelId = channel.sid;
        this.widgetUpdated.next(this.widget);
        this._cd.detectChanges();
      });
  }

  public editTitle(): void {
    this.changeTitle = true;
  }

  public reloadChat(event: boolean): void {
    if (event) {
      this.showMembers = false;
    }
    this._loadChannel();
    this._cd.detectChanges();
  }

  public setTitle(value: boolean): void {
    this.changeTitle = value;
    this._cd.detectChanges();
  }

  public setUpdateTitle(value: string): void {
    this.widget.name = value;
    this._cd.detectChanges();
  }

  public createDirectMessage(user: SnUser): void {
    const channelData = {
      users: [user.id],
    };
    this._channelsService
      .createDirectChannel(channelData)
      .pipe(
        switchMap((c) => {
          return this._chatService.getTwilioChannel(c.sid).pipe(
            switchMap((tc) => {
              c.twilioChannel = tc;
              this._chatService.setActiveChannel(c.sid);
              return of(c);
            }),
          );
        }),
        takeUntil(this._destroy$),
      )
      .subscribe((channel) => {
        if (channel.twilioChannel) {
          window.open(`/chat/${channel.twilioChannel.sid}`);
        }
      });
  }

  private _loadChannel(): void {
    this._chatService.setActiveChannel(this._channelId);
    this.channel = this._chatService.activeChannel;
    this._checkUserAccess();
    this._cd.detectChanges();
  }

  private _checkUserAccess(): void {
    this._membersService
      .fetchMemberList(this.channel.sid, { pageSize: 'all' })
      .pipe(takeUntil(this._destroy$))
      .subscribe((value) => {
        this.channel.members = new SnCollection<SnChatUser>(...value.results);
        this.userHasAccess = this.channel.members.some((user) => this._currentUser.id === user.id);
        this._cd.detectChanges();
      });
  }
}
